import React, { useEffect, useRef } from "react";
import { Box } from "../design-system";
import { colors } from "../design-system/colors";
import { SettingsIcon, LogoutIcon } from "../design-system/icons";
import { DROPDOWN } from "./constants/accountText";
import DropdownHeader from "./components/DropdownHeader";
import Divider from "../design-system/Divider";
import DropdownMenuItem from "./components/DropdownMenuItem";

interface AccountDropdownProps {
  isOpen: boolean;
  username: string;
  planLabel?: string;
  onAccountSettings: () => void;
  onLogout: () => void;
  onClose: () => void;
}

const AccountDropdown = ({
  isOpen,
  username,
  planLabel = "FREE",
  onAccountSettings,
  onLogout,
  onClose,
}: AccountDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <Box
      ref={dropdownRef}
      sx={{
        position: "absolute",
        top: "calc(100% + 8px)",
        right: 0,
        backgroundColor: colors.dropdown.background,
        borderRadius: "8px",
        boxShadow: "2px 2px 15px 0px rgba(0, 0, 0, 0.2)",
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "16px 12px",
      }}
      role="menu"
      aria-label="Account menu"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", width: "203px" }}>
        <DropdownHeader username={username} planLabel={planLabel} />
        <Divider color={colors.dropdown.divider} />
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <DropdownMenuItem
            icon={<SettingsIcon size={19} color={colors.neutral.white} />}
            label={DROPDOWN.ACCOUNT_SETTINGS}
            onClick={onAccountSettings}
          />
          <DropdownMenuItem
            icon={<LogoutIcon size={19} color={colors.neutral.white} />}
            label={DROPDOWN.LOGOUT}
            onClick={onLogout}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AccountDropdown;
